/**
 * FillerWordToolbar - Top toolbar for filler word removal mode.
 * Compact layout optimized for space efficiency.
 * Follows Libretto's calm, editorial design system.
 */
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { 
    Box, 
    IconButton, 
    Button, 
    Menu, 
    MenuItem, 
    Checkbox,
    Divider,
    Typography,
    CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    KeyboardArrowUp as ArrowUpIcon,
    KeyboardArrowDown as ArrowDownIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { FillerWordStore, FillerWordCutMode } from './FillerWordStore';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';

const CUT_MODE_LABELS: Record<FillerWordCutMode, string> = {
    'ignore': 'Ignore (Strikethrough)',
    'delete': 'Delete',
    'gap': 'Replace with gap',
    'transcript-only': 'Remove from transcript',
};

interface FillerWordToolbarProps {
    onRemoveCurrent: () => void;
    onRemoveAll: () => void;
    onClose: () => void;
}

const FillerWordToolbar: React.FC<FillerWordToolbarProps> = observer(({
    onRemoveCurrent,
    onRemoveAll,
    onClose,
}) => {
    const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
    const [modeAnchor, setModeAnchor] = useState<null | HTMLElement>(null);
    
    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchor(event.currentTarget);
    };
    
    const handleFilterClose = () => {
        setFilterAnchor(null);
    };
    
    const handleModeClick = (event: React.MouseEvent<HTMLElement>) => {
        setModeAnchor(event.currentTarget);
    };
    
    const handleModeClose = () => {
        setModeAnchor(null);
    };
    
    const handleModeSelect = (mode: FillerWordCutMode) => {
        FillerWordStore.setCutMode(mode);
        handleModeClose();
    };
    
    const handleNavigate = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            FillerWordStore.goToPrevious();
        } else {
            FillerWordStore.goToNext();
        }
        
        // Seek to the current occurrence - map source time through segments
        const current = FillerWordStore.currentOccurrence;
        if (current) {
            const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(current.startSec);
            if (compTime !== null) {
                CompositionService.seek(compTime);
            } else {
                CompositionService.seek(current.startSec);
            }
        }
    };
    
    const categories = FillerWordStore.categoriesWithCounts;
    const repeatedCount = FillerWordStore.categoryCounts.get('repeated') || 0;
    const isLoading = FillerWordStore.isRemoving || FillerWordStore.isAnalyzing;
    
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#fff',
                padding: '14px 16px',
                paddingRight: '40px', // Space for close button
                gap: 1.5,
                minHeight: 56,
            }}
        >
            {/* Close button - top right */}
            <IconButton 
                size="small" 
                onClick={onClose}
                sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    padding: '4px',
                    color: '#9CA3AF',
                    '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
            
            {/* Filter dropdown */}
            <Box
                data-tour="filler-filter-select"
                onClick={handleFilterClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    padding: '6px 8px',
                    borderRadius: '8px',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: '#F3F4F6' },
                }}
            >
                <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {FillerWordStore.allCategoriesSelected ? 'All filler words' : 'Selected'}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
            </Box>
            
            {/* Counter and navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 50 }}>
                        <CircularProgress size={12} sx={{ color: '#9CA3AF' }} />
                        <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                            {FillerWordStore.isRemoving 
                                ? `${FillerWordStore.removalProgress}%`
                                : `${FillerWordStore.analysisProgress}%`
                            }
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500, minWidth: 40 }}>
                        {FillerWordStore.removableCount > 0 
                            ? `${FillerWordStore.currentIndex + 1} / ${FillerWordStore.removableCount}`
                            : '0 / 0'
                        }
                    </Typography>
                )}
                
                <IconButton 
                    size="small" 
                    onClick={() => handleNavigate('prev')}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{ 
                        padding: '4px',
                        color: '#9CA3AF',
                        '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    <ArrowUpIcon sx={{ fontSize: 18 }} />
                </IconButton>
                
                <IconButton 
                    size="small" 
                    onClick={() => handleNavigate('next')}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{ 
                        padding: '4px',
                        color: '#9CA3AF',
                        '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    <ArrowDownIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            
            {/* Cut mode dropdown */}
            <Box
                onClick={handleModeClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: '#F9FAFB', borderColor: '#D1D5DB' },
                }}
            >
                <Typography sx={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                    {CUT_MODE_LABELS[FillerWordStore.cutMode].split(' ')[0]}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
            </Box>
            
            {/* Smooth cuts pill toggle with harsh count below */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box
                    onClick={() => FillerWordStore.toggleAvoidHarshCuts()}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '999px',
                        backgroundColor: FillerWordStore.avoidHarshCuts ? '#EFF6FF' : 'transparent',
                        border: FillerWordStore.avoidHarshCuts ? '1px solid #BFDBFE' : '1px solid #E5E7EB',
                        transition: 'all 0.15s ease',
                        '&:hover': { 
                            backgroundColor: FillerWordStore.avoidHarshCuts ? '#DBEAFE' : '#F3F4F6',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: FillerWordStore.avoidHarshCuts ? '#2B6BFD' : '#D1D5DB',
                            transition: 'background-color 0.15s ease',
                        }}
                    />
                    <Typography sx={{ 
                        fontSize: 12, 
                        color: FillerWordStore.avoidHarshCuts ? '#1D4ED8' : '#6B7280',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                    }}>
                        Smooth cuts
                    </Typography>
                </Box>
                
                {/* Harsh cut warning - positioned below, doesn't affect layout */}
                {FillerWordStore.harshCount > 0 && FillerWordStore.avoidHarshCuts && (
                    <Typography 
                        sx={{ 
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: '4px',
                            fontSize: 11, 
                            color: '#DC2626', 
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {FillerWordStore.harshCount} harsh
                    </Typography>
                )}
            </Box>
            
            {/*/!* Spacer *!/*/}
            {/*<Box sx={{ flex: 1 }} />*/}
            
            {/* Action buttons - stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0 }}>
                <Button
                    variant="text"
                    size="small"
                    onClick={onRemoveCurrent}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{
                        color: '#6B7280',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '6px 14px',
                        borderRadius: '8px',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                        },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    Remove
                </Button>
                <Button
                    data-tour="filler-remove-all"
                    variant="text"
                    size="small"
                    onClick={onRemoveAll}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{
                        color: '#6B7280',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '6px 14px',
                        borderRadius: '8px',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                        },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    Remove all ({FillerWordStore.removableCount})
                </Button>
            </Box>
            
            {/* Filter menu */}
            <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={handleFilterClose}
                PaperProps={{
                    sx: {
                        minWidth: 220,
                        maxHeight: 450,
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#D1D5DB transparent',
                        '&::-webkit-scrollbar': {
                            width: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#D1D5DB',
                            borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#9CA3AF',
                        },
                        boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                        borderRadius: '10px',
                        border: '1px solid #E5E7EB',
                        mt: 1,
                    }
                }}
            >
                {/* Select all */}
                <MenuItem 
                    onClick={() => {
                        if (FillerWordStore.allCategoriesSelected) {
                            FillerWordStore.deselectAllCategories();
                        } else {
                            FillerWordStore.selectAllCategories();
                        }
                    }}
                    sx={{ py: 1.25, px: 2 }}
                >
                    <Checkbox
                        size="small"
                        checked={FillerWordStore.allCategoriesSelected}
                        indeterminate={FillerWordStore.someCategoriesSelected}
                        sx={{ mr: 1.5, p: 0 }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Select all</Typography>
                </MenuItem>
                
                <Divider sx={{ my: 0.5 }} />
                
                {/* Repeated words */}
                <MenuItem 
                    onClick={() => FillerWordStore.toggleRepeatedWords()}
                    sx={{ py: 1.25, px: 2 }}
                >
                    <Checkbox
                        size="small"
                        checked={FillerWordStore.includeRepeatedWords}
                        sx={{ mr: 1.5, p: 0 }}
                    />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>Repeated words</Typography>
                    {repeatedCount > 0 && (
                        <Typography sx={{ fontSize: 12, color: '#9CA3AF', ml: 2 }}>
                            {repeatedCount}
                        </Typography>
                    )}
                </MenuItem>
                
                <Divider sx={{ my: 0.5 }} />
                
                {/* Individual categories */}
                {categories.map(category => (
                    <MenuItem
                        key={category.id}
                        onClick={() => FillerWordStore.toggleCategory(category.id)}
                        sx={{ py: 1, px: 2, pl: 4 }}
                    >
                        <Checkbox
                            size="small"
                            checked={category.enabled}
                            sx={{ mr: 1.5, p: 0 }}
                        />
                        <Typography sx={{ fontSize: 13, flex: 1 }}>{category.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#9CA3AF', ml: 2 }}>
                            {category.count}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
            
            {/* Cut mode menu */}
            <Menu
                anchorEl={modeAnchor}
                open={Boolean(modeAnchor)}
                onClose={handleModeClose}
                PaperProps={{
                    sx: {
                        minWidth: 200,
                        boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                        borderRadius: '10px',
                        border: '1px solid #E5E7EB',
                        mt: 1,
                    }
                }}
            >
                {(Object.keys(CUT_MODE_LABELS) as FillerWordCutMode[]).map(mode => (
                    <MenuItem
                        key={mode}
                        onClick={() => handleModeSelect(mode)}
                        selected={FillerWordStore.cutMode === mode}
                        sx={{ 
                            py: 1.25, 
                            px: 2,
                            fontSize: 13,
                            '&.Mui-selected': {
                                backgroundColor: '#F3F4F6',
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: '#E5E7EB',
                            },
                        }}
                    >
                        {CUT_MODE_LABELS[mode]}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
});

export default FillerWordToolbar;
